import CancelIcon from "@mui/icons-material/Cancel";
import ChatIcon from "@mui/icons-material/Chat";
import EditSquareIcon from "@mui/icons-material/EditSquare";
import LogoutIcon from "@mui/icons-material/Logout";
import Badge from "@mui/material/Badge";
import Portal from "@mui/material/Portal";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import LoadingButton from "_shared/_views/loading/loading-button";
import Guid from "_shared/guids/guid";
import { getForumsByTopic, populateForumsByTopic } from "messaging/forums/_stores/forums-redux-slice";
import Topic from "messaging/forums/topics/topic";
import ForumEntityClass from "notifications/values/forum-entity-class/forum-entity-class";
import { enqueueSnackbar } from "notistack";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { MaskedTeam } from "work/_shared/masked-fields";
import {
  ActionsContainer,
  ButtonContainer,
  Content,
  DialogTab,
  Header,
  MessageButton,
  MessageButtonLabel,
  MessageButtons,
  SecondaryActions,
  TabContent,
  TabsContainer,
} from "work/_views/work-dialog-styled-components";
import {
  endWorkAgreement,
  getWorkAgreementById,
  quitWorkTeam,
  reviseWorkAgreement,
} from "work/work-agreements/_store/work-agreement-redux-slice";
import ChatDrawer from "work/work-agreements/_views/chat-drawer";
import ConflictsTab from "work/work-agreements/_views/dialogs/tabs/conflicts-tab";
import DetailsTab from "work/work-agreements/_views/dialogs/tabs/details-tab";
import DiscountTab from "work/work-agreements/_views/dialogs/tabs/discount-tab";
import FeeScheduleTab from "work/work-agreements/_views/dialogs/tabs/fee-schedule-tab";
import InvoicesTab from "work/work-agreements/_views/dialogs/tabs/invoices-tab";
import PoliciesTab from "work/work-agreements/_views/dialogs/tabs/policies-tab";
import TeamTab from "work/work-agreements/_views/dialogs/tabs/team-tab";
import { WorkAgreementFieldCategory } from "work/work-agreements/work-agreement";

const WorkAgreementActions = styled("section")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexWrap: "nowrap",
  },
  alignContent: "end",
  alignItems: "center",
  display: "grid",
  flex: 1,
  gridTemplateColumns: "repeat(auto-fit, minmax(15rem, 1fr))",
  gap: theme.spacing(1),
  minHeight: "64px",
}));
const WorkAgreementActionSpan = styled("span")(({ theme }) => ({
  minWidth: theme.spacing(24),
  width: "100%",
}));
const WorkAgreementActionButton = styled(LoadingButton)(() => ({
  width: "100%",
  whiteSpace: "nowrap",
  "&.Mui-disabled": {
    color: "rgba(0, 0, 0, 0.26) !important",
    backgroundColor: "rgba(0, 0, 0, 0.12) !important",
  },
}));

function adjustDialogPosition(panelOpen: boolean) {
  for (const dialog of document.getElementsByClassName("MuiDialog-root")) {
    dialog.setAttribute("style", `padding-right: ${panelOpen ? "0px" : "400px"}; transition: padding-right 225ms;`);
  }
}

type WorkAgreementDialogProps = {
  workAgreementId?: Guid;
  readOnly?: boolean;
};

export default function WorkAgreementDialog(props: Readonly<WorkAgreementDialogProps>) {
  const { workAgreementId, readOnly } = props;

  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const confirm = useConfirmDialog();
  const { closeDialog } = useDialog();

  const [activeTab, setActiveTab] = React.useState<WorkAgreementFieldCategory>(WorkAgreementFieldCategory.Details);
  const [isRevising, setIsRevising] = React.useState(false);
  const [isEnding, setIsEnding] = React.useState(false);
  const [isLeaving, setIsLeaving] = React.useState(false);
  const [isChatPanelOpen, setIsChatPanelOpen] = React.useState(false);

  const workAgreement = useSelector(getWorkAgreementById(workAgreementId));
  const topic = Topic.create({ entityClass: new ForumEntityClass("Work.Agreement"), entityId: workAgreementId });
  const forums = useSelector(getForumsByTopic(topic));

  useEffect(() => {
    dispatch(populateForumsByTopic({ session, topic }));
  }, [dispatch, session, topic, workAgreementId]);

  function handleTabChange(_event: React.SyntheticEvent | null, newTab: WorkAgreementFieldCategory) {
    setActiveTab(newTab);
  }

  function handleCloseChatPanel() {
    setIsChatPanelOpen(false);
    adjustDialogPosition(true);
  }

  function handleOpenChatPanel() {
    setIsChatPanelOpen(true);
    adjustDialogPosition(false);
  }

  async function handleEndWorkAgreement() {
    if (!workAgreement?.id) {
      console.error("Work agreement not found");
      return;
    }

    const response = await confirm({
      title: "End Work Agreement?",
      message:
        "This will void the work agreement for any future work and only work performed " +
        "before this date will be billable until a new proposal is agreed to.",
      okButtonText: "End",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      setIsEnding(true);

      dispatch(endWorkAgreement({ session, id: workAgreement.id }));
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to end work agreement", { variant: "error" });
    } finally {
      setIsEnding(false);
    }
  }

  async function handleReviseWorkAgreement() {
    if (!workAgreement?.id) {
      throw new Error("Work agreement not found");
    }
    if (!session.user) {
      throw new Error("User not found");
    }

    const response = await confirm({
      title: "Revise Work Agreement?",
      message:
        "This will void the work agreement for any future work and only work performed " +
        "before this date will be billable until a new proposal is agreed to.",
      okButtonText: "Revise",
    });

    if (response === ConfirmResponse.Cancel) return;
    setIsRevising(true);
    await dispatch(reviseWorkAgreement({ session, workAgreement }));
    setIsRevising(false);
  }

  async function handleLeaveTeam() {
    if (!workAgreement?.id) {
      console.error("Work agreement not found");
      return;
    }
    if (!session.user?.id) {
      console.error("User not found");
      return;
    }

    const response = await confirm({
      title: "Leave Team?",
      message: "Are you sure you want to leave the vendor-team?",
      okButtonText: "Leave",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      setIsLeaving(true);
      await dispatch(quitWorkTeam({ session, workAgreementId: workAgreement.id }));
      enqueueSnackbar("You have left the vendor-team", { variant: "success" });
      closeDialog();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to leave vendor-team", { variant: "error" });
    } finally {
      setIsLeaving(false);
    }
  }

  return (
    <>
      <Header>
        <TabsContainer
          variant="scrollable"
          scrollButtons="auto"
          indicatorColor="primary"
          textColor="primary"
          value={activeTab}
          onChange={handleTabChange}
        >
          <DialogTab value={WorkAgreementFieldCategory.Details} label="Details" />
          <DialogTab value={WorkAgreementFieldCategory.Team} label="Team" />
          <DialogTab value={WorkAgreementFieldCategory.FeeSchedule} label="Fee Schedule" />
          <DialogTab value={WorkAgreementFieldCategory.Conflicts} label="Conflicts" />
          <DialogTab value={WorkAgreementFieldCategory.Policies} label="Policies" />
          <DialogTab value={WorkAgreementFieldCategory.Discount} label="Discount" />
          <DialogTab value={WorkAgreementFieldCategory.Invoices} label="Invoices" />
        </TabsContainer>
      </Header>
      <Content>
        <TabContent>
          {workAgreement && (
            <>
              <DetailsTab workAgreement={workAgreement} activeTab={activeTab} />
              <TeamTab workAgreement={workAgreement} activeTab={activeTab} />
              <FeeScheduleTab workAgreement={workAgreement} activeTab={activeTab} />
              <ConflictsTab workAgreement={workAgreement} activeTab={activeTab} />
              <PoliciesTab workAgreement={workAgreement} activeTab={activeTab} />
              <DiscountTab workAgreement={workAgreement} activeTab={activeTab} />
              <InvoicesTab workAgreement={workAgreement} activeTab={activeTab} />
            </>
          )}
        </TabContent>
      </Content>
      <ActionsContainer>
        <SecondaryActions />
        <WorkAgreementActions>
          {((MaskedTeam.isPresentNotMasked(workAgreement?.clientTeam) &&
            "userId" in workAgreement.clientTeam &&
            workAgreement.clientTeam.leader?.userId.isEqualTo(session.user?.id)) ||
            (MaskedTeam.isPresentNotMasked(workAgreement?.vendorTeam) &&
              workAgreement?.vendorTeam?.leader &&
              "userId" in workAgreement.vendorTeam.leader &&
              workAgreement.vendorTeam.leader.userId.isEqualTo(session.user?.id))) && (
            <Tooltip title="Create New Proposal" placement="top">
              <WorkAgreementActionSpan>
                <WorkAgreementActionButton
                  variant="contained"
                  color="primary"
                  startIcon={<EditSquareIcon />}
                  loading={isRevising}
                  disabled={readOnly || isEnding || isRevising || isLeaving}
                  onClick={handleReviseWorkAgreement}
                >
                  Revise
                </WorkAgreementActionButton>
              </WorkAgreementActionSpan>
            </Tooltip>
          )}
          {MaskedTeam.isPresentNotMasked(workAgreement?.clientTeam) &&
            workAgreement.clientTeam.leader?.userId.isEqualTo(session.user?.id) && (
              <Tooltip title="End Work Agreement" placement="top">
                <WorkAgreementActionSpan>
                  <WorkAgreementActionButton
                    variant="contained"
                    color="error"
                    startIcon={<CancelIcon />}
                    loading={isEnding}
                    disabled={readOnly || isEnding || isRevising || isLeaving}
                    onClick={handleEndWorkAgreement}
                  >
                    End Agreement
                  </WorkAgreementActionButton>
                </WorkAgreementActionSpan>
              </Tooltip>
            )}
          {MaskedTeam.isPresentNotMasked(workAgreement?.vendorTeam) &&
            workAgreement?.vendorTeam?.members.some((member) => member.userId.isEqualTo(session.user?.id)) && (
              <WorkAgreementActionButton
                variant="contained"
                color="primary"
                startIcon={<LogoutIcon />}
                loading={isLeaving}
                disabled={readOnly || isEnding || isRevising || isLeaving}
                onClick={handleLeaveTeam}
              >
                Leave Team
              </WorkAgreementActionButton>
            )}
        </WorkAgreementActions>
        <MessageButtons>
          <ButtonContainer>
            <MessageButton size="medium" color="primary" onClick={handleOpenChatPanel}>
              <Badge variant="standard" color="secondary" overlap="rectangular">
                <ChatIcon fontSize="medium" />
              </Badge>
            </MessageButton>
            <MessageButtonLabel variant="button" color={workAgreementId ? "primary" : "darkgray"}>
              Messages
            </MessageButtonLabel>
          </ButtonContainer>
        </MessageButtons>
      </ActionsContainer>
      <Portal>
        <ChatDrawer
          isOpen={isChatPanelOpen}
          isArchived={workAgreement?.isArchived}
          forums={forums ?? []}
          onOpen={handleOpenChatPanel}
          onClose={handleCloseChatPanel}
        />
      </Portal>
    </>
  );
}
